import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Button, InlineNotice, TextLink, formatCount, useDebouncedValue } from '@wit-support/ui';
import { useZodFormContext } from '@wit-support/ui-forms';
import { CircleAlert } from 'lucide-react';
import { batchSpend } from '../api.js';
import { formatMinor } from '../format.js';
import { planIssues } from '../issues.js';
import { ADS_QUERY_DEFAULTS } from '../query-policy.js';
import { ADS_KEY } from '../shared.js';
import { FieldIssues, RegistryFields } from '../registry/registry-form.js';
import { fieldName } from '../registry/to-zod.js';
import { SpendSummary, spendIncomplete, spendTotalText } from './spend-summary.js';
/*
 * Структура запуска (спека «Конструктор FB v4» §11.1–§11.2, без групп):
 * лесенка ставок копий (одна граница по направлению шага) и блок «Получится»
 * — форма плана и деньги в день от сервера (`POST /batches/spend`, задержка
 * 300 мс после правки). Клиент ничего не умножает: суммы и формулы — из ответа.
 */
/** Опции лесенки ставок копий (v4 §3.3) — у пачки свои поля входа `bidStep*`. */
export const LADDER_KEYS = [
    'opt.bidStepMinor',
    'opt.bidStepDirection',
    'opt.bidFloorMinor',
    'opt.bidCeilingMinor',
];
/** Поле входа пачки по ключу опции лесенки. */
export const LADDER_INPUT_FIELD = {
    'opt.bidStepMinor': 'bidStepMinor',
    'opt.bidStepDirection': 'bidStepDirection',
    'opt.bidFloorMinor': 'bidFloorMinor',
    'opt.bidCeilingMinor': 'bidCeilingMinor',
};
/** Стратегии, при которых работает шаг ставки копий (v4 §3.3, `bid-step-without-cap`). */
export const BID_STEP_STRATEGIES = new Set(['BID_CAP', 'COST_CAP']);
export const BID_STEP_NEEDS_CAP_TEXT = 'Шаг ставки работает только со стратегией «Предельная ставка» или «Цель по цене»';
export const BID_STEP_NO_CEILING_TEXT = 'Без «не выше» ставка растёт с каждой копией';
export const BID_STEP_ONE_COPY_TEXT = 'Шаг ставки действует при 2 копиях и больше';
/** «Получится» без ответа сервера: денег клиентской формулой не считаем. */
export const SPEND_AFTER_CHECK_TEXT = 'Итог — после «Проверить»';
/**
 * Опция лесенки сейчас действует (Codex, PR #340): при стратегии без ставки —
 * ни одна; иначе шаг и направление, а граница — только по направлению шага
 * («вверх» — «не выше», «вниз» — «не ниже»). Неактивную форма не проверяет и
 * не отправляет (запуск), значение в форме сохраняется. Не опция лесенки — true.
 */
export function ladderKeyActive(key, live) {
    if (!LADDER_KEYS.includes(key))
        return true;
    if (!BID_STEP_STRATEGIES.has(String(live['campaign.bidStrategy'] ?? '')))
        return false;
    const down = live['opt.bidStepDirection'] === 'down';
    if (key === 'opt.bidFloorMinor')
        return down;
    if (key === 'opt.bidCeilingMinor')
        return !down;
    return true;
}
/** Лесенка в живых значениях: шаг (minor), вниз ли, ставка работает ли (стратегия с капом). */
export function ladderState(live) {
    const step = typeof live['opt.bidStepMinor'] === 'number' ? live['opt.bidStepMinor'] : 0;
    return {
        step,
        down: live['opt.bidStepDirection'] === 'down',
        cap: BID_STEP_STRATEGIES.has(String(live['campaign.bidStrategy'] ?? '')),
        ceiling: live['opt.bidCeilingMinor'],
    };
}
/**
 * Поля лесенки (v4 §11.1 «Лесенка», §11.2): видны только при «Предельной
 * ставке» / «Цели по цене»; граница — одна, по направлению шага («вверх» —
 * «Не выше», «вниз» — «Не ниже»). Шаг задан, а стратегия без ставки — ошибка
 * и [Убрать шаг ставки]; иначе серая строка. Вторая граница остаётся в форме
 * (не стирается): сервер её игнорирует.
 */
export function LadderFields({ descs, prefix, live, issues, copies, }) {
    const { form } = useZodFormContext();
    const by = new Map(descs.map((d) => [d.key, d]));
    if (!by.has('opt.bidStepMinor'))
        return null;
    const { step, down, cap, ceiling } = ladderState(live);
    if (!cap) {
        return step > 0 ? (_jsx(InlineNotice, { variant: "danger", bordered: true, icon: CircleAlert, as: "div", "data-testid": "bid-ladder-needs-cap", children: _jsxs("div", { className: "flex flex-col gap-2", children: [_jsxs("span", { children: [BID_STEP_NEEDS_CAP_TEXT, ". \u0423\u0431\u0435\u0440\u0438\u0442\u0435 \u0448\u0430\u0433 \u0438\u043B\u0438 \u0441\u043C\u0435\u043D\u0438\u0442\u0435 \u0441\u0442\u0440\u0430\u0442\u0435\u0433\u0438\u044E."] }), _jsx(Button, { type: "button", size: "sm", variant: "secondary", className: "w-full sm:w-fit", onClick: () => form.setValue(`${prefix}.${fieldName('opt.bidStepMinor')}`, 0, { shouldDirty: true }), children: "\u0423\u0431\u0440\u0430\u0442\u044C \u0448\u0430\u0433 \u0441\u0442\u0430\u0432\u043A\u0438" })] }) })) : (_jsx("span", { className: "text-xs text-[var(--color-text-muted)]", "data-testid": "bid-ladder-needs-cap", children: BID_STEP_NEEDS_CAP_TEXT }));
    }
    const shown = [
        by.get('opt.bidStepMinor'),
        by.get('opt.bidStepDirection'),
        by.get(down ? 'opt.bidFloorMinor' : 'opt.bidCeilingMinor'),
    ].filter((d) => d != null);
    return (_jsxs("div", { className: "flex flex-col gap-2", "data-testid": "bid-ladder", children: [_jsx(RegistryFields, { descriptors: shown, prefix: prefix, issues: issues, contextValues: live }), step > 0 && copies === 1 ? (_jsx("span", { className: "text-xs text-[var(--color-text-muted)]", "data-testid": "bid-ladder-one-copy", children: BID_STEP_ONE_COPY_TEXT })) : null, step > 0 && !down && ceiling == null ? (_jsx("span", { className: "text-xs text-[var(--color-text-muted)]", "data-testid": "bid-ladder-no-ceiling", children: BID_STEP_NO_CEILING_TEXT })) : null] }));
}
// --- деньги до «Проверить» -----------------------------------------------------------------
/** Пауза после правки шагов 2–5 перед запросом денег (v4 §11.0). */
export const SPEND_DEBOUNCE_MS = 300;
const OFF = {
    enabled: false,
    data: undefined,
    pending: false,
    failed: false,
    invalid: false,
};
const LaunchSpendContext = createContext(OFF);
/** Деньги до «Проверить» для шага 5 и липкого футера (один запрос на экран). */
export function useLaunchSpend() {
    return useContext(LaunchSpendContext);
}
/**
 * Запрашивает деньги в день по входу формы (тот же вход и ключ
 * идемпотентности, что у preview → те же уникальные центы) с задержкой 300 мс.
 * `input` null — считать нечего (нет кабинетов или креативов).
 */
export function LaunchSpendProvider({ enabled, input, invalid = false, children, }) {
    const raw = input != null ? JSON.stringify(input) : '';
    const key = useDebouncedValue(raw, SPEND_DEBOUNCE_MS);
    const req = useMemo(() => (key ? JSON.parse(key) : null), [key]);
    const q = useQuery({
        ...ADS_QUERY_DEFAULTS,
        queryKey: [...ADS_KEY, 'batch-spend', req],
        queryFn: () => batchSpend(req),
        enabled: enabled && req != null,
        placeholderData: keepPreviousData,
        retry: false,
        staleTime: 30_000,
    });
    const state = enabled
        ? {
            enabled,
            data: input == null || q.isError ? undefined : q.data,
            pending: input != null && (key !== raw || q.isFetching),
            failed: q.isError,
            invalid,
        }
        : OFF;
    return _jsx(LaunchSpendContext.Provider, { value: state, children: children });
}
/** Подсказка вместо итога, пока поле денег / структуры с ошибкой. */
export const SPEND_FORM_INVALID_TEXT = 'Исправьте отмеченное поле — итог пересчитаем';
/** Итог дня для футера: «$783.12» (по валютам через « + »); нет годного — null. */
export function spendFooterText(data) {
    if (!data || !data.spend.differsFromField || spendIncomplete(data.spend))
        return null;
    const lines = data.spend.lines.filter((l) => l.usable !== false);
    if (lines.length === 0)
        return null;
    const total = lines.map(spendTotalText).join(' + ');
    return `В день всего: ${total} · ${formatCount(data.structure.campaigns, ['кампания', 'кампании', 'кампаний'])}`;
}
/** «1 кампания · 3 группы объявлений · 3 объявления (1-3-1)». */
export function structureText(s) {
    const shape = s.shapes.length > 0 ? ` (${s.shapes.join(', ')})` : '';
    return [
        formatCount(s.campaigns, ['кампания', 'кампании', 'кампаний']),
        formatCount(s.adSets, ['группа объявлений', 'группы объявлений', 'групп объявлений']),
        formatCount(s.ads, ['объявление', 'объявления', 'объявлений']),
    ]
        .join(' · ')
        .concat(shape);
}
/** Ставка каждой копии по плану сервера (копия с 0 → ставка); разных меньше двух — null. */
export function copyBids(preview) {
    const by = new Map();
    let currency = null;
    // разные валюты кабинетов — ставки копий одной строкой не показать (ревью SB-4 P2-2)
    if (new Set(preview.launches.map((l) => l.currency)).size > 1)
        return null;
    for (const l of preview.launches) {
        for (const as of l.adSets) {
            const copy = as.copyIndex ?? l.copyIndex;
            const bid = as.bidAmountMinor ?? l.bidAmountMinor;
            if (bid == null || by.has(copy))
                continue;
            by.set(copy, bid);
            currency ??= l.currency;
        }
    }
    const copies = [...by.entries()]
        .sort(([a], [b]) => a - b)
        .map(([copy, bidMinor]) => ({ copy, bidMinor }));
    if (copies.length < 2 || new Set(copies.map((c) => c.bidMinor)).size < 2 || currency == null) {
        return null;
    }
    return { copies, currency };
}
/** Замечания плана про лесенку и имена — под «Получится» (v4 §11.2, тексты §4.1 с сервера). */
export const STRUCTURE_ISSUE_CODES = new Set([
    'bid-step-without-cap',
    'bid-step-clamped',
    'bid-step-large',
    'adset-name-numbered',
    'name-shape-literal',
]);
/**
 * «Получится» в три строки (v4 §11.2): структура одной кампании, число
 * кампаний с множителями, итог (`SpendSummary`). Числа — из ответа сервера;
 * множители («2 кабинета × 3 креатива») — подписи формы, показываются, только
 * если их произведение совпало с числом кампаний сервера.
 */
export function structureLines(s, c) {
    const shape = s.shapes.length === 1 ? ` (${s.shapes[0]})` : '';
    const even = s.shapes.length === 1 &&
        s.campaigns > 0 &&
        s.adSets % s.campaigns === 0 &&
        s.ads % s.campaigns === 0;
    const perCampaign = even
        ? `В каждой кампании: ${formatCount(s.adSets / s.campaigns, ['группа объявлений', 'группы объявлений', 'групп объявлений'])} · ${formatCount(s.ads / s.campaigns, ['объявление', 'объявления', 'объявлений'])}${shape}`
        : structureText(s);
    const parts = [
        [c.accounts, ['кабинет', 'кабинета', 'кабинетов']],
    ];
    if (c.layout === 'campaign_per_item') {
        parts.push([c.items, ['креатив', 'креатива', 'креативов']]);
        parts.push([c.copies, ['копия', 'копии', 'копий']]);
    }
    else if (c.layout === 'ads_in_adset') {
        parts.push([c.copies, ['копия', 'копии', 'копий']]);
    }
    const shown = parts.filter(([n]) => n > 1);
    const product = parts.reduce((acc, [n]) => acc * n, 1);
    const factors = shown.length > 0 && product === s.campaigns
        ? ` (${shown.map(([n, f]) => formatCount(n, f)).join(' × ')})`
        : '';
    return { perCampaign, campaigns: `Кампаний: ${s.campaigns}${factors}` };
}
/**
 * «Получится» шага «5. Структура» (v4 §11.2): структура одной кампании, число
 * кампаний, ставки копий и итог в день — всё от сервера. Ставки копий — из
 * свежего плана «Проверить» (маршрут денег их не считает); до него — шаг словами.
 */
export function StructureOutcome({ preview, ladder, currency, maxAdSetsPerCampaign, counts, onSwitchLayout, templateId, hideBids = false, }) {
    const spend = useLaunchSpend();
    if (!spend.enabled)
        return null;
    const data = spend.data;
    const money = (minor) => formatMinor(minor, currency);
    const bids = preview ? copyBids(preview) : null;
    const stepText = ladder ? `шаг ${ladder.down ? '−' : '+'}${money(ladder.step)}` : '';
    const tooMany = data != null && data.structure.maxAdSetsPerCampaign > maxAdSetsPerCampaign;
    const lines = data ? structureLines(data.structure, counts) : null;
    const perItem = counts.layout === 'campaign_per_item';
    const bidsLabel = perItem ? 'Ставки по кампаниям' : 'Ставки';
    const notes = preview ? planIssues(preview).filter((i) => STRUCTURE_ISSUE_CODES.has(i.code)) : [];
    const unique = [...new Map(notes.map((i) => [`${i.code}|${i.message}`, i])).values()];
    return (_jsxs("section", { className: "flex min-w-0 flex-col gap-2 rounded-[var(--radius-2)] border border-[var(--color-border-subtle)] p-3 [overflow-wrap:anywhere]", "aria-label": "\u041F\u043E\u043B\u0443\u0447\u0438\u0442\u0441\u044F", "data-testid": "structure-outcome", children: [_jsx("span", { className: "text-sm font-medium", children: "\u041F\u043E\u043B\u0443\u0447\u0438\u0442\u0441\u044F" }), spend.invalid ? (_jsx("p", { className: "text-sm text-[var(--color-state-warning-fg)]", "data-testid": "structure-invalid", children: SPEND_FORM_INVALID_TEXT })) : null, lines ? (_jsxs("div", { className: spend.pending ? 'flex flex-col gap-0.5 opacity-60' : 'flex flex-col gap-0.5', "data-testid": "structure-shape", children: [_jsx("p", { className: "text-sm", children: lines.perCampaign }), _jsx("p", { className: "text-sm", children: lines.campaigns })] })) : null, ladder && !hideBids ? (bids ? (_jsxs("div", { className: "flex flex-col gap-0.5", "data-testid": "structure-bids", children: [_jsxs("p", { className: "text-base font-semibold tabular-nums", children: [bidsLabel, ": ", formatMinor(bids.copies[0].bidMinor, bids.currency), " \u2192", ' ', formatMinor(bids.copies[bids.copies.length - 1].bidMinor, bids.currency), " (", stepText, ", ", formatCount(bids.copies.length, ['копия', 'копии', 'копий']), ")"] }), _jsx("p", { className: "text-sm tabular-nums text-[var(--color-text-secondary)]", children: bids.copies
                            .map((c) => `копия ${c.copy + 1} ${formatMinor(c.bidMinor, bids.currency)}`)
                            .join(' · ') })] })) : (_jsx("p", { className: "text-sm", "data-testid": "structure-bids", children: ladder.bidMinor != null
                    ? `${bidsLabel}: ${stepText} от общей ставки ${money(ladder.bidMinor)} — ставку каждой копии покажет «Проверить»`
                    : `${bidsLabel}: ${stepText} от общей ставки (укажите ставку в шаге 4)` }))) : null, perItem && counts.copies > 1 && onSwitchLayout ? (_jsxs("div", { className: "flex flex-wrap items-center gap-2 text-sm", "data-testid": "structure-switch-layout", children: [_jsxs("span", { children: ["\u041D\u0443\u0436\u043D\u0430 \u043E\u0434\u043D\u0430 \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u044F 1-", counts.copies, "-1?"] }), _jsx(Button, { type: "button", size: "sm", variant: "secondary", className: "w-full sm:w-fit", onClick: onSwitchLayout, children: "\u041F\u0435\u0440\u0435\u043A\u043B\u044E\u0447\u0438\u0442\u044C \u0440\u0430\u0441\u043A\u043B\u0430\u0434\u043A\u0443" })] })) : null, tooMany ? (_jsxs(InlineNotice, { variant: "danger", icon: CircleAlert, as: "div", "data-testid": "structure-too-many", children: ["\u041F\u043E\u043B\u0443\u0447\u0438\u0442\u0441\u044F ", data.structure.maxAdSetsPerCampaign, " \u0433\u0440\u0443\u043F\u043F \u043E\u0431\u044A\u044F\u0432\u043B\u0435\u043D\u0438\u0439 \u0432 \u043E\u0434\u043D\u043E\u0439 \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u0438 \u2014 \u0431\u043E\u043B\u044C\u0448\u0435", ' ', maxAdSetsPerCampaign, ". \u0423\u043C\u0435\u043D\u044C\u0448\u0438\u0442\u0435 \u0447\u0438\u0441\u043B\u043E \u043A\u043E\u043F\u0438\u0439 \u0438\u043B\u0438 \u043A\u0440\u0435\u0430\u0442\u0438\u0432\u043E\u0432"] })) : null, _jsx(SpendSummary, { spend: spend.invalid ? null : (data?.spend ?? null), state: spend.invalid
                    ? 'unavailable'
                    : spend.pending
                        ? 'pending'
                        : data == null
                            ? 'unavailable'
                            : 'ready' }), !spend.invalid && data && data.issues.length > 0 ? (_jsx(FieldIssues, { issues: data.issues })) : null, unique.length > 0 ? (_jsx("ul", { className: "flex flex-col gap-1", "data-testid": "structure-notes", children: unique.map((i) => (_jsxs("li", { className: "flex flex-col gap-1", children: [_jsx(FieldIssues, { issues: [i] }), i.code === 'name-shape-literal' && templateId ? (_jsx(TextLink, { asChild: true, className: "text-sm", children: _jsx(Link, { to: "/fb-launches/templates/$id", params: { id: templateId }, target: "_blank", rel: "noreferrer", children: "\u041E\u0442\u043A\u0440\u044B\u0442\u044C \u0448\u0430\u0431\u043B\u043E\u043D \u2197" }) })) : null] }, `${i.code}|${i.message}`))) })) : null] }));
}
//# sourceMappingURL=launch-structure.js.map